
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {
  attachmentIcon, attachmentUrl, formatSize, IEmailAttachment, isPreviewImage, listAttachments, saveAttachment,
  visibleAttachments,
} from '@/services/helpdeskEmailAttachments';

@Component({name: 'EmailAttachments'})
export default class EmailAttachments extends Vue {
  @Prop({required: true}) emailId!: number;
  // From the email itself; null/undefined for emails imported before attachments were tracked.
  @Prop({default: null}) attachments!: IEmailAttachment[] | null;

  list: IEmailAttachment[] = [];
  loading = false;
  failed = false;
  urls: {[partId: string]: string} = {};
  errors: {[partId: string]: boolean} = {};
  busy: string | null = null;
  viewerOpen = false;
  viewing: IEmailAttachment | null = null;
  formatSize = formatSize;
  private request = 0;

  get visible(): IEmailAttachment[] {
    return visibleAttachments(this.list);
  }

  get images(): IEmailAttachment[] {
    return this.visible.filter(isPreviewImage);
  }

  get files(): IEmailAttachment[] {
    return this.visible.filter((a) => !isPreviewImage(a));
  }

  icon(a: IEmailAttachment) {
    return attachmentIcon(a);
  }

  @Watch('emailId', {immediate: true})
  @Watch('attachments')
  load() {
    const request = ++this.request;
    this.urls = {};
    this.errors = {};
    this.failed = false;

    if (Array.isArray(this.attachments)) {
      this.list = this.attachments;
      this.loadThumbnails(request);
      return;
    }

    // Older email: the server completes its attachment list from Gmail the first time.
    this.list = [];
    this.loading = true;
    listAttachments(this.emailId)
      .then((list) => {
        if (request !== this.request) return;
        this.list = list;
        this.loadThumbnails(request);
      })
      .catch(() => { if (request === this.request) this.failed = true; })
      .then(() => { if (request === this.request) this.loading = false; });
  }

  private loadThumbnails(request: number) {
    this.images.forEach((a) => {
      attachmentUrl(this.emailId, a)
        .then((url) => { if (request === this.request) this.$set(this.urls, a.part_id, url); })
        .catch(() => { if (request === this.request) this.$set(this.errors, a.part_id, true); });
    });
  }

  openImage(a: IEmailAttachment) {
    if (!this.urls[a.part_id]) return;
    this.viewing = a;
    this.viewerOpen = true;
  }

  openFile(a: IEmailAttachment) {
    if (a.mime !== 'application/pdf') {
      this.download(a);
      return;
    }
    // Open the tab now (still inside the click) so the popup blocker lets it through.
    const tab = window.open('', '_blank');
    this.busy = a.part_id;
    attachmentUrl(this.emailId, a)
      .then((url) => { if (tab) tab.location.href = url; })
      .catch(() => { if (tab) tab.close(); this.$set(this.errors, a.part_id, true); })
      .then(() => { this.busy = null; });
  }

  download(a: IEmailAttachment) {
    this.busy = a.part_id;
    saveAttachment(this.emailId, a)
      .catch(() => this.$set(this.errors, a.part_id, true))
      .then(() => { this.busy = null; });
  }
}
